import { useEffect, useState } from "react";
import { Check, Timer } from "lucide-react";
import { QuizStudent } from "@/modes/quiz/QuizStudent";
import { QuizTeacher } from "@/modes/quiz/QuizTeacher";
import { Final, TeamScores } from "@/modes/quiz/QuizBoard";

const KIND = { cinko: "ÇİNKO", tombala: "TOMBALA" };
const teamOf = (state, id) => state.teams.find((t) => t.id === id);

// Zardaki "İpucu çek" yazısını yok edip sadece ikonu bırakan güçlü temizleyici
const useCleanDiceButton = () => {
  useEffect(() => {
    const timer = setInterval(() => {
      document.querySelectorAll("button").forEach(btn => {
        // Metin silindikten sonra da butonu tanıyabilmek için işaretliyoruz
        if (btn.textContent.includes("İpucu çek") || btn.dataset.cleaned === "true") {
          btn.dataset.cleaned = "true";
          
          Array.from(btn.childNodes).forEach(node => {
            if (node.nodeType === Node.TEXT_NODE && node.textContent.includes("İpucu çek")) {
              node.textContent = "";
            } else if (node.tagName === "SPAN" && node.textContent.includes("İpucu çek")) {
              node.style.display = "none";
            }
          });
          
          // Zar ikonunu tam ortaya hizalamak için buton stillerini zorla eziyoruz
          btn.style.display = "flex";
          btn.style.justifyContent = "center";
          btn.style.alignItems = "center";
          btn.style.gap = "0";
          
          // İkonun kendisindeki margin (sağa/sola itme) değerlerini sıfırlıyoruz
          const svg = btn.querySelector("svg");
          if (svg) svg.style.margin = "0";
          
          // Butonun asimetrik dolgularını ezip, ikonu tam merkeze alan şık bir genişlik veriyoruz
          btn.style.paddingLeft = "0";
          btn.style.paddingRight = "0";
          btn.style.width = "4.5rem";
        }
      });
    }, 50);
    return () => clearInterval(timer);
  }, []);
};

const Wins = ({ state, big }) => (
  <div className={`grid ${big ? "grid-cols-1 gap-[2vh] w-[26vw] ml-auto" : "grid-cols-2 gap-2 items-start"}`} data-testid="bingo-wins">
    {Object.entries(KIND).map(([k, label]) => (
      <div key={k} className={`panel ${big ? "p-[1.5vh]" : "p-3"}`}>
        <p className={`font-display font-bold text-[#FACC15] ${big ? "text-[3vh]" : "text-base"}`}>{label}</p>
        {(state.wins?.[k] || []).map((w) => {
          const t = teamOf(state, w.team_id);
          return <p key={w.place} className={big ? "text-[2.4vh]" : "text-sm"}><b>{w.place}.</b> <span style={{ color: t?.color }}>{t?.name || "Masasız"}</span> masasından · +{w.points}</p>;
        })}
        {!(state.wins?.[k] || []).length && <p className={`text-slate-500 ${big ? "text-[2.2vh]" : "text-sm"}`}>Henüz yok</p>}
      </div>
    ))}
  </div>
);

export const BingoStudent = ({ state, send, remaining }) => {
  useCleanDiceButton();
  if (state.phase !== "activity") return <QuizStudent state={state} send={send} remaining={remaining} />;
  return (
    <div className="space-y-5" data-testid="student-bingo">
      <div className="panel p-4">
        <p className="eyebrow">Çekilen ipucu · {state.drawn_count}</p>
        <p className="text-xl font-bold mt-1" data-testid="student-bingo-clue">{state.current_clue || "Çekiliş bekleniyor…"}</p>
      </div>
      <div className="grid grid-cols-3 gap-2" data-testid="bingo-card">
        {state.card.map((c, i) => (
          <button key={i} onClick={() => !c.marked && send({ type: "mark", cell: i })} data-testid={`bingo-cell-${i}`}
            className={`aspect-square rounded-xl border-2 grid place-items-center p-1 text-center font-bold text-lg break-words transition-colors duration-150 ${c.marked ? "bg-[#FACC15] border-[#FACC15] text-[#05070D]" : "border-[rgba(100,116,139,0.6)] bg-white/5"}`}>
            {c.marked ? <span className="flex flex-col items-center"><Check className="h-5 w-5" />{c.answer}</span> : c.answer}
          </button>
        ))}
      </div>
      {state.my_wins?.length > 0 && <p className="text-center font-display text-2xl font-black text-[#34D399]" data-testid="bingo-my-wins">{state.my_wins.map((k) => KIND[k]).join(" + ")}!</p>}
      <p className="text-sm text-slate-400">Puanın: <b className="text-[#FACC15]" data-testid="student-total">{state.my_total}</b> · Yanlış işaret sunucuda reddedilir.</p>
      <Wins state={state} />
    </div>
  );
};

const AutoCountdown = ({ state }) => {
  const [left, setLeft] = useState(state.next_draw_ms);
  useEffect(() => {
    if (state.next_draw_ms == null || state.paused) { setLeft(state.next_draw_ms); return undefined; }
    const end = Date.now() + state.next_draw_ms;
    const id = setInterval(() => setLeft(Math.max(0, end - Date.now())), 250);
    setLeft(state.next_draw_ms);
    return () => clearInterval(id);
  }, [state.next_draw_ms, state.drawn_count, state.paused]);
  if (!state.auto_draw) return null;
  return (
    <span className="flex items-center gap-2 text-[2.6vh] font-semibold text-[#FDE047]" data-testid="board-auto-countdown">
      <Timer className="h-[3vh] w-[3vh]" />Sonraki çekiliş: {state.paused ? "duraklatıldı" : `${Math.ceil(left / 1000)} sn`}
    </span>
  );
};

export const BingoBoard = ({ state }) => {
  useCleanDiceButton();
  if (state.phase === "finished") return <Final state={state} />;
  
  const clueText = state.current_clue || "Çekiliş başlamak üzere";
  // Boyutları çok daha devasa yaptık!
  const textSizeClass = clueText.length > 14 ? "text-[min(6rem,9vh)]" : "text-[min(12rem,18vh)]";

  return (
    <div className="h-full flex flex-col gap-[2.5vh]" data-testid="board-bingo">
      <div className="flex items-center gap-6 z-10">
        <span className="font-mono-x text-[2.4vh] text-slate-400">Çekilen {state.drawn_count} / {state.item_count}</span>
        <span className="text-[2.4vh] text-slate-300 flex-1">{state.activity_title}</span>
        <AutoCountdown state={state} />
      </div>
      
      {/* ORTA ALAN: Relative wrapper ile ana kelimeyi ızgaradan (grid) tamamen bağımsız kıldık */}
      <div className="flex-1 min-h-0 relative flex">
        
        {}
        <div className="w-full flex justify-between h-full">
          <div className="flex items-end pb-[2vh] flex-1 pr-[4vw]">
            {state.recent_clues?.length > 0 && (
              <p className="text-[3.5vh] text-slate-300 font-medium">
                Önceki: {state.recent_clues.join(" · ")}
              </p>
            )}
          </div>
          <div className="shrink-0">
            <Wins state={state} big />
          </div>
        </div>

        {/* EKRANIN KUSURSUZ MERKEZİ (Absolute ile sağdaki paneli umursamaz, ekranın tam ortasına oturur) */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <p className={`font-display font-bold leading-tight text-center px-4 drop-shadow-2xl ${textSizeClass}`} 
             style={{ textShadow: "0 10px 40px rgba(0,0,0,0.9)" }} data-testid="board-bingo-clue">
            {clueText}
          </p>
        </div>

      </div>

      <div className="z-10">
        <TeamScores state={state} />
      </div>
    </div>
  );
};

export const BingoTeacher = ({ state, send }) => {
  useCleanDiceButton();
  return (
    <div className="space-y-6">
      {state.drawn_items && (
        <div className="panel p-5 space-y-3" data-testid="teacher-bingo">
          <h3 className="font-display text-xl font-bold">Çekilenler · {state.drawn_items.length}/{state.item_count}</h3>
          <div className="flex flex-wrap gap-2">{state.drawn_items.map((i) => <span key={i.id} className="rounded-md bg-white/5 px-2 py-1 text-sm"><b>{i.answer}</b> <span className="text-slate-400">· {i.clue}</span></span>)}</div>
          {Object.entries(KIND).map(([k, label]) => (
            <p key={k} className="text-sm"><b className="text-[#FACC15]">{label}:</b> {(state.wins?.[k] || []).map((w) => {
              const s = state.students.find((x) => x.id === w.sid);
              return `${w.place}. ${s ? `${s.first_name} ${s.last_name}` : "(ayrıldı)"} (+${w.points})`;
            }).join(", ") || "—"}</p>
          ))}
        </div>
      )}
      <QuizTeacher state={state} send={send} />
    </div>
  );
};
